import React, { useCallback, useEffect, useState } from 'react';
import AppLoader from 'components/general/AppLoader';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import MeetingsCardBig from './MeetingsCardBig';
import MeetingsCard from './MeetingsCard';
import { toast } from 'react-toastify';
import AgendaToolbar from '../Meetings/AgendaToolbar';
import { useSelector } from 'react-redux';

const MeetingsComponent = ({ config }) => {
    const PAGE_SIZE = 20;
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [nextMeeting, setNextMeeting] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalMeetings, setTotalMeetings] = useState(0);

    const [toggleColor, setToggleColor] = useState('primary');
    const [isNoSleep, setIsNoSleep] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const toggleWakeOnScreen = (e) => {
        e.preventDefault();
        setIsNoSleep((prev) => {
            const next = !prev;
            setToggleColor(next ? 'danger' : 'primary');
            if (!next && toggleColor === 'danger') { window.location.reload(false); }
            return next;
        });
    };
    const url = process.env.REACT_APP_URL;
    const { user: authUser } = useSelector(x => x.auth);

    const getNextMeeting = useCallback(async () => {
        try {
            const response = await fetch(`${url}/meetings/comingup`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {})
                }
            });
            const data = await response.json();
            if (data && data.Message === 'No next meetings found') {
                setNextMeeting(null); // Explicitly set to null if no next meeting is found
            } else {
                setNextMeeting(data);
            }
        } catch (e) {
            console.log(`Meetings component - Next Meeting error: ${e}`);
        }
    }, [url, authUser?.token]);

    const getMeetings = useCallback(async ({ page = 1, append = false } = {}) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsInitialLoading(true);
        }
        try {
            const response = await fetch(`${url}/meetings?page=${page}&limit=${PAGE_SIZE}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {})
                }
            });
            const data = await response.json();

            if (data && Array.isArray(data.data)) {
                setMeetings((previousMeetings) => {
                    if (!append) {
                        return data.data;
                    }

                    const existingIds = new Set(previousMeetings.map((meeting) => meeting._id));
                    const uniqueNewMeetings = data.data.filter((meeting) => !existingIds.has(meeting._id));
                    return [...previousMeetings, ...uniqueNewMeetings];
                });
                setTotalMeetings(data.total || 0); // Store the total number of meetings from the API response
            } else {
                toast.error('Failed to load meetings. Unexpected data format. Check API response.', { position: 'bottom-left' });
            }
        } catch (error) {
            toast.error(`Error fetching meetings: ${error.message}`, { position: 'bottom-left' });
        } finally {
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsInitialLoading(false);
            }
        }
    }, [url, authUser?.token]);

    useEffect(() => {
        getMeetings({ page: 1, append: false });
        getNextMeeting();
    }, [getMeetings, getNextMeeting]);

    const handleLoadMore = async () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        await getMeetings({ page: nextPage, append: true });
    };

    const refreshMeetings = useCallback(async () => {
        setCurrentPage(1);
        await getMeetings({ page: 1, append: false });
    }, [getMeetings]);

    useEffect(() => {
        const handleExternalRefresh = () => refreshMeetings();
        window.addEventListener('meetings:refresh', handleExternalRefresh);
        return () => window.removeEventListener('meetings:refresh', handleExternalRefresh);
    }, [refreshMeetings]);

    if (isInitialLoading) {
        return (
            <section className="meetings" id="meetings">
                <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AppLoader />
                </div>
            </section>
        );
    } else {
        return (
            <Container className="meetings-page-container">
                <AgendaToolbar
                    toggleColor={toggleColor}
                    isNoSleep={isNoSleep}
                    toggleWakeOnScreen={toggleWakeOnScreen}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    getMeetings={refreshMeetings}
                />
                <Row className='mt-2'>
                    <Col xs={12} lg={12}>
                        <Card className='meetings-title-card'>
                            <Card.Body>
                                <h3 className='meetings-title-card-text'>All Meetings</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {/* <Row className='mb-1' style={{ width: '90%', margin: '0 auto' }}>
                    <Col xs={12} lg={12} style={{ position: 'relative' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search for a meeting..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchTerm && (
                            <button
                                className="btn btn-light btn-sm"
                                style={{ position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%)', padding: 0 }}
                                onClick={() => setSearchTerm('')}
                            >
                                <i className="bi bi-x-circle"></i>
                            </button>
                        )}
                    </Col>
                </Row> */}
                <div className="meetings-page-scroll">
                    <Row className='meeting-cards user-card-list'>
                        {meetings.length === 0 ? (
                            <Col>
                                <p className="text-center">No meetings found</p>
                            </Col>
                        ) : (
                            meetings.map(meeting => (
                                meeting._id === nextMeeting?._id ? (
                                    <MeetingsCardBig
                                        meeting={meeting}
                                        getMeetings={refreshMeetings}
                                        editMode={editMode}
                                        displayRole={meeting.meetingCardDisplayRole || config?.meetingCardDisplayRole || (Array.isArray(config?.meetingCardDisplayRoles) ? config.meetingCardDisplayRoles[0] : '')}
                                        key={meeting._id}
                                    />
                                ) : (
                                    <MeetingsCard
                                        meeting={meeting}
                                        getMeetings={refreshMeetings}
                                        editMode={editMode}
                                        displayRole={meeting.meetingCardDisplayRole || config?.meetingCardDisplayRole || (Array.isArray(config?.meetingCardDisplayRoles) ? config.meetingCardDisplayRoles[0] : '')}
                                        key={meeting._id}
                                    />
                                )
                            ))
                        )}
                    </Row>
                    <Row className="mt-3 mb-2">
                        <Col className="text-center">
                            <span className="me-2">Showing {meetings.length} of {totalMeetings}</span>
                            {meetings.length < totalMeetings && (
                                <Button variant="secondary" onClick={handleLoadMore} disabled={isLoadingMore}>
                                    {isLoadingMore ? 'Loading...' : 'Load 20 More'}
                                </Button>
                            )}
                        </Col>
                    </Row>
                    {isLoadingMore && (
                        <Row className="mb-3">
                            <Col className="text-center">
                                <AppLoader />
                            </Col>
                        </Row>
                    )}
                </div>
            </Container>
        );
    }
};

export default MeetingsComponent;